import dropRight from 'lodash/dropRight';
import get from 'lodash/get';
import last from 'lodash/last';
// eslint-disable-next-line jira/import-whitelist
import { addSpanToAll } from '@atlaskit/react-ufo/interaction-metrics';
import { setMark } from '@atlassian/jira-common-performance/src/marks.tsx';
import logger from '@atlassian/jira-common-util-logging/src/log.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
// eslint-disable-next-line jira/import-whitelist
import { prefetchApiReadyMark } from '@atlassian/jira-providers-spa-apdex-analytics/src/marks.tsx';
import { createResourceError } from '@atlassian/jira-router-resources-utils/src/utils/create-resource-error/index.tsx';
import { getProjectKeyId } from '@atlassian/jira-router-resources-utils/src/utils/get-project-key-id/index.tsx';

import { checkAuthentication } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication/index.tsx';

import { checkLicense } from '@atlassian/jira-spa-apps-common/src/interceptors/license/index.tsx';
import type { RouterDataContext, ResourceStoreContext } from '@atlassian/react-resource-router';

import type { CategorizedQueueData, CategorizedQueueRestData } from '../../types.tsx';
import { transformRestData } from '../../utils/index.tsx';

const MAX_ATTEMPTS = 3;

class RetriedError extends Error {
	previousErrors: Error[];

	constructor(retryErrors: Error[] = []) {
		// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
		super(last(retryErrors).message);
		this.previousErrors = dropRight(retryErrors);
	}
}

const getCategories = async (
	baseUrl: string,
	projectKey: string,
	failedAttempts: Error[] = [],
): Promise<CategorizedQueueData> => {
	try {
		const fetchStartTime = performance.now();

		const data: CategorizedQueueRestData = await fetchJson(
			`${baseUrl}/rest/servicedesk/1/servicedesk/${projectKey}/queues/categories`,
		);

		addSpanToAll(
			'fetch',
			'/rest/servicedesk/1/servicedesk/projectKey/queues/categories',
			[{ name: 'network' }],
			fetchStartTime,
			performance.now(),
		);

		setMark(prefetchApiReadyMark);
		return transformRestData(data, projectKey);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		logger.safeErrorWithoutCustomerData(
			'jiraServicedeskQueuesNavigationItemsResource',
			error.message,
			{
				statusCode: error.statusCode,
				retryCount: failedAttempts.length,
			},
		);

		fireErrorAnalytics({
			meta: {
				id: 'jiraServicedeskQueuesNavigationItemsResource',
				packageName: 'jiraSpa',
			},
			error,
			attributes: {
				retryCount: failedAttempts.length,
			},
		});

		failedAttempts.push(error);

		if (failedAttempts.length >= MAX_ATTEMPTS || !(error.statusCode >= 500)) {
			throw new RetriedError(failedAttempts);
		}

		return getCategories(baseUrl, projectKey, failedAttempts);
	}
};

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default ({ match, query }: RouterDataContext, { tenantContext }: ResourceStoreContext) => {
	// Early exit on these cases and return a valid response. We don't want to flood sentry.
	if (!checkAuthentication(tenantContext)) {
		throw createResourceError({
			component: 'spa.view.routes.servicedesk.queues.getData',
			message: 'Current user is not authenticated',
			name: 'AuthenticationError',
			skipSentry: true,
		});
	}

	// Resource is shared with non-JSM projects, so invoke initial RRR state on missing servicedesk license
	if (!checkLicense(tenantContext, 'servicedesk')) {
		return null;
	}

	// projectKey from url path/query or browse/{issueKey}
	const projectKey =
		getProjectKeyId(match, query).projectKey || get(match.params, 'issueKey', '')?.split('-')[0];

	if (projectKey == null) {
		return null;
	}

	return getCategories(tenantContext.baseUrl, projectKey);
};
